import { askLeoOpenRouter, parseLeoOpenRouterJson } from "@/lib/leo-openrouter"
import { classificationCategoryChoices, parseLeoClassificationPayload, type LeoClassificationPayload } from "@/lib/question-quality/classification-ask"
import type { ExamCategory } from "@/lib/exam-categories"

export async function suggestQuestionCategories(input: {
  stem: string
  topic?: string
  bloom?: string
  categories: ExamCategory[]
}): Promise<Array<{ id: string; label: string; reason: string; fit: "high" | "medium" | "low" }>> {
  const choices = classificationCategoryChoices(input.categories)
  const byId = new Map(choices.map((choice) => [choice.id, choice.label]))
  const response = await askLeoOpenRouter({
    json: true,
    messages: [
      {
        role: "system",
        content: "Suggest up to three categories for this assessment question from the supplied catalog only, best first. Prefer the most specific supported child category. Consider what knowledge and decision the learner must demonstrate, not merely words in the stem: a word that appears in a category name is not enough, and a question about a different subject must not be placed because of a shared word. Return JSON {candidates:[{id, reason, fit}]} where reason is one short question-specific sentence and fit is high only when the central skill or knowledge the question tests is exactly this category, medium when it is a reasonable home, low when it is only loosely related. A subject the catalog has no category for (for example cell biochemistry in a nursing catalog) gets no category or a low fit, never a high one. If the question does not support any category, return an empty array. Never invent a category id.",
      },
      {
        role: "user",
        content: `Question: ${input.stem}\nCurrent topic: ${input.topic ?? "None"}\nBloom: ${input.bloom ?? "None"}\nAvailable categories: ${choices.map((choice) => `${choice.id}: ${choice.label}`).join("; ")}`,
      },
    ],
  })
  const parsed = parseLeoOpenRouterJson<{ candidates?: unknown }>(response)
  if (!Array.isArray(parsed?.candidates)) return []
  return parsed.candidates.flatMap((candidate) => {
    if (!candidate || typeof candidate !== "object") return []
    const row = candidate as { id?: unknown; reason?: unknown; fit?: unknown }
    if (typeof row.id !== "string" || !byId.has(row.id)) return []
    const reason = typeof row.reason === "string" ? row.reason.trim() : ""
    if (!reason) return []
    const fit: "high" | "medium" | "low" = row.fit === "high" || row.fit === "low" ? row.fit : "medium"
    return [{ id: row.id, label: byId.get(row.id)!, reason, fit }]
  }).filter((candidate, index, all) => all.findIndex((other) => other.id === candidate.id) === index).slice(0, 3)
}

export async function suggestQuestionClassification(input: {
  stem: string
  bloom?: string
  difficulty?: string
  categoryIds?: string[]
  categories: ExamCategory[]
}): Promise<LeoClassificationPayload | null> {
  const choices = classificationCategoryChoices(input.categories)
  const response = await askLeoOpenRouter({
    json: true,
    messages: [
      {
        role: "system",
        content: "Classify this question without rewriting it. Return JSON only with bloom, difficulty, categoryId, bloomReason, difficultyReason, categoryReason, and categoryCandidates. Bloom must be Remember, Understand, Apply, Analyze, Evaluate, or Create. Difficulty must be easy, medium, or hard. Choose the primary category by the knowledge or skill the student needs to answer. Put that category first in categoryCandidates, followed by up to two defensible alternatives with question-specific reasons. Use only category IDs in the catalog; do not force alternatives that do not fit.",
      },
      {
        role: "user",
        content: `Question: ${input.stem}\nCurrent Bloom: ${input.bloom ?? "None"}\nCurrent difficulty: ${input.difficulty ?? "None"}\nCurrent categories: ${input.categoryIds?.join(", ") || "None"}\nCatalog: ${choices.map((choice) => `${choice.id}: ${choice.label}`).join("; ")}`,
      },
    ],
  })
  return parseLeoClassificationPayload(parseLeoOpenRouterJson<unknown>(response), input.categories)
}
